import { useMutation, useQueryClient } from '@tanstack/react-query';
import useAppToast from '@/hooks/useAppToast';
import { updateConsent } from '@/services/main/consent';
import { useClinicContext } from './useClinicContext';
import { useClinicPortalServiceType } from './clinicPortalServiceType';
import { getApiErrorMessage } from '@/utils/apiError';
import { invalidateVisitClinicScheduleForPatient } from './useVisitClinic';

interface UseUpdateConsentMutationProps {
  patientId: string;
  consentId: string;
}

export function useUpdateConsentMutation({ patientId, consentId }: UseUpdateConsentMutationProps) {
  const { tenant, clinicNumber } = useClinicContext();
  const serviceType = useClinicPortalServiceType();
  const queryClient = useQueryClient();
  const toast = useAppToast();

  return useMutation({
    mutationFn: async (data: unknown) => {
      const res = await updateConsent(tenant, clinicNumber, patientId, consentId, data, serviceType);
      if (!res?.success) {
        const err: Error & { response?: { status: number; data: unknown } } = new Error(
          res?.message || '更新に失敗しました。'
        );
        err.response = { status: 422, data: res };
        throw err;
      }
      return res;
    },
    onSuccess: () => {
      toast.success('更新が正常に終了しました。');
      queryClient.invalidateQueries({
        queryKey: ['consents', 'list', tenant, clinicNumber, patientId],
        refetchType: 'active',
      });
      queryClient.invalidateQueries({
        queryKey: ['consents', 'detail', tenant, clinicNumber, patientId, consentId, serviceType],
        refetchType: 'active',
      });
      void invalidateVisitClinicScheduleForPatient(queryClient, patientId);
    },
    onError: (error: unknown) => {
      const status = (error as { response?: { status?: number } })?.response?.status;
      if (status === 422) {
        return;
      }
      toast.error(getApiErrorMessage(error, '更新ができませんでした。'));
    },
  });
}
